import { useCallback, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  buildHeaderFilterFields,
  BROWSER_TRACKER_UI_CAPABILITIES,
  computeReadiness,
  createTrackerFilterFields,
  getRecordStatus,
  getRecordTitle,
  getTrackerHeaderFilterValue,
  resolveColumnsForType,
  resolveViewMode,
  PersonalClauseNotice,
  TagBoard,
  TRACKER_VIEW_MODE_OPTIONS,
  TrackerActiveFilterPills,
  TrackerBoardSurface,
  TrackerFilterOmnibox,
  TrackerGridSurface,
  TrackerListView,
  TrackerMutationRejectionNotice,
  TrackerRadarView,
  TrackerSurfaceMessage,
  TrackerTimelineView,
  TrackerViewHeaderControls,
  useTrackerCommand,
  useTrackerDataSelector,
  useTrackerViewRows,
  type SavedViewDefinition,
  type TrackerFilterSet,
  type TrackerGridUpdateEntry,
  type TrackerViewLayoutUpdate,
} from '@nimbalyst/collab-bundle/trackers-ui';
import {
  RECENT_ACTIVITY_VIEW_ID,
  orderByTeamRecency,
  resolveTrackerTypeAvailability,
} from './trackerViewState';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { useTrackerSelection, useTrackersShell } from './TrackersShell';
import { useTrackerRowMenu } from './useTrackerRowMenu';
import { phonePresentation, useTrackerBrowseState } from './TrackerBrowseState';
import { usePhoneTrackerLayout } from './usePhoneTrackerLayout';
import { PhoneTrackerToolbar } from './PhoneTrackerToolbar';
import './phoneTrackers.css';

/** One shape for every control on the inline create row. */
const CREATE_FIELD =
  'h-7 min-w-0 rounded border border-nim bg-nim text-[0.75rem] text-nim';

/** What the body says when it has nothing legitimate to draw. */
const UNAVAILABLE_MESSAGE = {
  pending: {
    icon: 'hourglass_top',
    message: 'Opening this tracker…',
    hint: 'Waiting for the team’s tracker schema to arrive.',
    testId: 'trackers-type-pending',
  },
  unshared: {
    icon: 'link_off',
    message: 'This tracker is no longer shared with the team.',
    hint: 'It was unshared or deleted. Trackers the team still shares are listed on the left.',
    testId: 'trackers-type-unshared',
  },
  indeterminate: {
    icon: 'cloud_off',
    message: 'This tab cannot reach the tracker room.',
    hint: 'Whether this tracker still exists cannot be answered while the room is disconnected.',
    testId: 'trackers-type-indeterminate',
  },
} as const;

/**
 * Which modes this surface can honestly draw.
 *
 * The triage inbox is deliberately out: its snooze suppression is personal
 * state, so a browser tab would show items the desktop hides -- a wrong answer,
 * not a missing affordance. Capabilities keep that host difference explicit,
 * and `resolveViewMode` is the single place the question gets answered -- the
 * picker below, the definition normalizer, and the substitution notice all read
 * it, so the control can never offer a mode the body substitutes away.
 */
const CONSOLE_CAPABILITIES = BROWSER_TRACKER_UI_CAPABILITIES;

/** Offer exactly the modes that would not be substituted. */
const CONSOLE_VIEW_MODE_OPTIONS = TRACKER_VIEW_MODE_OPTIONS.filter(
  (option) => !resolveViewMode(option.value, CONSOLE_CAPABILITIES).substituted,
);

/** The one copy of what the notice says when a mode was substituted. */
const SUBSTITUTION_NOTICE =
  'This view opens as a list; the triage inbox is not available in the browser.';

const ROW_EDITABLE = () => true;

export function TrackerViewSurface() {
  const { navigate, itemPath, identity, schema, projectId, activeItemId } = useTrackersShell();
  const { definition, activeSavedViewId, selectedType } = useTrackerSelection();
  const records = useTrackerDataSelector(state => state.records);
  const loaded = useTrackerDataSelector(state => state.loaded);
  const sync = useTrackerDataSelector(state => state.sync);
  const rejection = useTrackerDataSelector(state => state.rejection);
  const command = useTrackerCommand();
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createType, setCreateType] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [openFiltersToken, setOpenFiltersToken] = useState(0);

  const creatableTypes = useMemo(
    () => schema.trackerTypes.filter((model) => (
      model.sharing === 'team' && model.creatable !== false && !model.archived
    )),
    [schema.trackerTypes],
  );

  const beginCreate = useCallback(() => {
    const active = creatableTypes.find((model) => model.type === selectedType);
    setCreateType(active?.type ?? creatableTypes[0]?.type ?? '');
    setCreateError(null);
    setCreateOpen(true);
  }, [creatableTypes, selectedType]);

  const createItem = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    const title = createTitle.trim();
    const model = creatableTypes.find((candidate) => candidate.type === createType);
    if (!title || !model || creating) return;
    const statusFieldName = model.roles?.workflowStatus ?? 'status';
    const priorityFieldName = model.roles?.priority ?? 'priority';
    const titleFieldName = model.roles?.title ?? 'title';
    const statusField = model.fields.find((field) => field.name === statusFieldName);
    const priorityField = model.fields.find((field) => field.name === priorityFieldName);
    const status = typeof statusField?.default === 'string'
      ? statusField.default
      : statusField?.options?.[0]?.value ?? 'to-do';
    const priority = typeof priorityField?.default === 'string' ? priorityField.default : 'medium';
    const customFields: Record<string, unknown> = {};
    if (titleFieldName !== 'title') customFields[titleFieldName] = title;
    if (statusFieldName !== 'status') customFields[statusFieldName] = status;
    if (priorityFieldName !== 'priority') customFields[priorityFieldName] = priority;
    setCreating(true);
    setCreateError(null);
    try {
      await command({
        type: 'create-item',
        item: {
          id: crypto.randomUUID(),
          type: model.type,
          title,
          status,
          priority,
          workspace: projectId,
          sharing: 'team',
          customFields,
        },
      });
      setCreateTitle('');
      setCreateOpen(false);
    } catch (cause) {
      setCreateError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setCreating(false);
    }
  }, [command, creatableTypes, createTitle, createType, creating, projectId]);

  const updateBoardItem = useCallback((item: { id: string }, updates: Record<string, unknown>) => (
    command({
      type: 'update-item',
      input: { itemId: item.id, updates, sharing: 'team' },
    })
  ), [command]);

  const updateGridItems = useCallback((entries: readonly TrackerGridUpdateEntry[]) => {
    if (entries.length === 1) {
      const entry = entries[0]!;
      return command({
        type: 'update-item',
        input: { itemId: entry.itemId, updates: entry.updates, sharing: 'team' },
      });
    }
    return command({
      type: 'update-items',
      input: {
        entries: entries.map((entry) => ({
          itemId: entry.itemId,
          storeUpdates: entry.updates,
          sharing: 'team' as const,
        })),
      },
    });
  }, [command]);

  const availability = resolveTrackerTypeAvailability({
    selectedType,
    knownTypes: schema.trackerTypes.map((model) => model.type),
    loaded,
    syncStatus: sync.status,
  });

  const { substituted } = resolveViewMode(definition.viewMode, CONSOLE_CAPABILITIES);
  const schemaType = selectedType === 'all' ? '' : selectedType;
  const { viewState, setViewState, browseEntry, browseKey } = useTrackerBrowseState(activeSavedViewId, definition);
  const phone = usePhoneTrackerLayout();
  const presentation = phonePresentation(viewState, phone);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Hiding the list for detail may clamp its scrollTop; capture it at row-open
  // and restore after the list is visible again, including after route remount.
  useLayoutEffect(() => {
    if (activeItemId) return;
    const list = bodyRef.current?.querySelector<HTMLElement>('.tracker-list-view');
    if (list) list.scrollTop = browseEntry.scrollTop;
  }, [activeItemId, browseKey, loaded, presentation, browseEntry]);
  const updateLayout = useCallback((updates: TrackerViewLayoutUpdate) => {
    setViewState((current) => ({ ...current, ...updates }));
  }, [setViewState]);
  const setColumnFilters = useCallback((columnFilters: TrackerFilterSet) => {
    setViewState((current) => ({ ...current, columnFilters }));
  }, [setViewState]);

  // One pass over the FULL corpus, not the filtered rows: readiness is a
  // dependency-graph property, and a blocker filtered out of the view still
  // blocks.
  const readinessByItemId = useMemo(
    () => computeReadiness(records, getRecordStatus),
    [records],
  );
  const filterContext = useMemo(
    () => ({ identity, readinessByItemId }),
    [identity, readinessByItemId],
  );
  const availableColumns = useMemo(
    () => resolveColumnsForType(schemaType),
    [schemaType],
  );
  const baseFilterFields = useMemo(
    () => createTrackerFilterFields(availableColumns, schemaType, schema.trackerTypes),
    [availableColumns, schema.trackerTypes, schemaType],
  );

  const effectiveDefinition = useMemo<SavedViewDefinition>(
    () => ({
      ...definition,
      viewMode: viewState.viewMode,
      groupBy: viewState.groupBy,
      ordering: viewState.ordering,
      columnConfig: viewState.columnConfig,
      columnFilters: viewState.columnFilters,
      statusScope: viewState.statusScope,
    }),
    [definition, viewState],
  );

  const { rows, personalClauses } = useTrackerViewRows(records, effectiveDefinition, {
    identity,
    readinessByItemId,
    searchTerm: viewState.searchTerm,
  });
  const unscopedDefinition = useMemo<SavedViewDefinition>(
    () => ({ ...effectiveDefinition, statusScope: 'all' }),
    [effectiveDefinition],
  );
  const { rows: unscopedRows } = useTrackerViewRows(records, unscopedDefinition, {
    identity,
    readinessByItemId,
    searchTerm: viewState.searchTerm,
  });
  const filterOptionDefinition = useMemo<SavedViewDefinition>(
    () => ({ ...effectiveDefinition, columnFilters: null }),
    [effectiveDefinition],
  );
  const { rows: filterOptionRows } = useTrackerViewRows(records, filterOptionDefinition, {
    identity,
    readinessByItemId,
  });
  const getFilterValue = useCallback(
    (item: typeof records[number], field: string) => getTrackerHeaderFilterValue(
      item,
      field,
      availableColumns,
      filterContext,
    ),
    [availableColumns, filterContext],
  );
  const filterFields = useMemo(
    () => buildHeaderFilterFields(baseFilterFields, filterOptionRows, getFilterValue),
    [baseFilterFields, filterOptionRows, getFilterValue],
  );
  const removeFilter = useCallback((index: number) => {
    setViewState((current) => ({
      ...current,
      columnFilters: {
        combinator: current.columnFilters?.combinator ?? 'and',
        clauses: (current.columnFilters?.clauses ?? []).filter((_, position) => position !== index),
      },
    }));
  }, [setViewState]);

  const orderedRows = useMemo(
    () => (activeSavedViewId === RECENT_ACTIVITY_VIEW_ID ? orderByTeamRecency(rows) : rows),
    [activeSavedViewId, rows],
  );
  const relationshipTitles = useMemo(
    () => new Map(records.map((record) => [record.id, getRecordTitle(record).trim()])),
    [records],
  );
  const resolveRelationshipLabel = useCallback(
    (itemId: string) => relationshipTitles.get(itemId) || undefined,
    [relationshipTitles],
  );

  const openItem = (itemId: string) => {
    const list = bodyRef.current?.querySelector<HTMLElement>('.tracker-list-view');
    if (list) browseEntry.scrollTop = list.scrollTop;
    navigate(itemPath(itemId));
  };
  const rowMenu = useTrackerRowMenu();

  return (
    <div className="trackers-view-surface flex min-h-0 flex-1 flex-col" data-view-mode={presentation}>
      {phone ? <PhoneTrackerToolbar state={viewState} setState={setViewState} fields={filterFields} definition={definition} columns={availableColumns} count={orderedRows.length} onCreate={creatableTypes.length ? beginCreate : undefined} /> : null}
      <div hidden={phone} className="trackers-view-toolbar flex min-h-[38px] shrink-0 items-center gap-[0.45rem] border-b border-nim px-3 py-1">
        <TrackerFilterOmnibox
          className="min-w-[180px] flex-1 max-w-[420px]"
          searchQuery={viewState.searchTerm}
          onSearchQueryChange={(searchTerm) => setViewState((current) => ({ ...current, searchTerm }))}
          fields={filterFields}
          filters={viewState.columnFilters}
          onFiltersChange={setColumnFilters}
          showPills={false}
        />
        <TrackerActiveFilterPills
          fields={filterFields}
          filters={viewState.columnFilters}
          onManage={() => setOpenFiltersToken((token) => token + 1)}
          onRemove={removeFilter}
        />
        <span className="trackers-view-spring flex-1" />
        <TrackerViewHeaderControls
          itemCount={orderedRows.length}
          unscopedItemCount={unscopedRows.length}
          availableColumns={availableColumns}
          columnConfig={viewState.columnConfig}
          onColumnConfigChange={(columnConfig) => setViewState((current) => ({ ...current, columnConfig }))}
          showColumnControls={viewState.viewMode === 'list' || viewState.viewMode === 'table'}
          filterFields={filterFields}
          filters={viewState.columnFilters}
          onFiltersChange={setColumnFilters}
          openFiltersToken={openFiltersToken}
          statusScope={viewState.statusScope}
          onStatusScopeChange={(statusScope) => setViewState((current) => ({ ...current, statusScope }))}
          viewMode={viewState.viewMode}
          groupBy={viewState.groupBy}
          ordering={viewState.ordering}
          onLayoutChange={updateLayout}
          viewModeOptions={CONSOLE_VIEW_MODE_OPTIONS}
        />
        {creatableTypes.length > 0 ? (
          <button
            type="button"
            className="trackers-create-button inline-flex h-[26px] cursor-pointer items-center gap-1 rounded border border-nim bg-nim-secondary px-[0.55rem] text-[0.72rem] text-nim hover:border-nim-focus hover:bg-nim-hover"
            onClick={beginCreate}
          >
            <DecorativeIcon icon="add" size={15} />
            New item
          </button>
        ) : null}
      </div>

      {createOpen ? (
        <form
          className="trackers-create-form grid shrink-0 grid-cols-[minmax(8rem,0.35fr)_minmax(12rem,1fr)_auto_auto] gap-[0.4rem] border-b border-nim bg-nim-secondary px-3 py-2"
          onSubmit={(event) => { void createItem(event); }}
        >
          <select
            className={`${CREATE_FIELD} px-2`}
            aria-label="Tracker type"
            value={createType}
            onChange={(event) => setCreateType(event.target.value)}
          >
            {creatableTypes.map((model) => (
              <option key={model.type} value={model.type}>{model.displayName}</option>
            ))}
          </select>
          <input
            className={`${CREATE_FIELD} px-2`}
            autoFocus
            value={createTitle}
            onChange={(event) => setCreateTitle(event.target.value)}
            placeholder="Item title"
            aria-label="Item title"
          />
          <button
            className={`${CREATE_FIELD} cursor-pointer border-[color:var(--nim-primary)] bg-nim-primary px-[0.65rem] text-[var(--nim-on-primary)] disabled:cursor-default disabled:opacity-45`}
            type="submit"
            disabled={!createTitle.trim() || creating}
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
          <button
            className={`${CREATE_FIELD} cursor-pointer px-[0.65rem]`}
            type="button"
            onClick={() => setCreateOpen(false)}
          >
            Cancel
          </button>
        </form>
      ) : null}
      {createError ? (
        <div className="trackers-create-error border-b border-[var(--nim-error)] px-3 py-1.5 text-[0.72rem] text-[var(--nim-error)]" role="alert">
          {createError}
        </div>
      ) : null}
      <TrackerMutationRejectionNotice rejection={rejection} />

      {substituted && (
        <div className="trackers-view-substitution shrink-0 border-b border-nim bg-nim-secondary px-3 py-1.5 text-[0.7rem] text-nim-muted" role="status">
          {SUBSTITUTION_NOTICE}
        </div>
      )}
      <PersonalClauseNotice clauses={personalClauses} />
      {rowMenu.notice ? (
        <div className="trackers-row-action-notice shrink-0 border-b border-nim px-3 py-1.5 text-[0.7rem] text-nim-muted" role="status">
          {rowMenu.notice}
        </div>
      ) : null}

      {rowMenu.element}
      <div ref={bodyRef} onScrollCapture={event => {
        if (!activeItemId && event.target instanceof HTMLElement && event.target.classList.contains('tracker-list-view')) browseEntry.scrollTop = event.target.scrollTop;
      }} className="trackers-view-body flex min-h-0 flex-1 flex-col overflow-hidden [&>*]:min-h-0 [&>*]:flex-1">
        {availability !== 'available' ? (
          <TrackerSurfaceMessage {...UNAVAILABLE_MESSAGE[availability]} />
        ) : loaded && records.length === 0 && sync.status === 'connected' ? (
          <TrackerSurfaceMessage
            icon="checklist"
            message="This tracker room has no items yet."
            hint="Create the first team item from the New item button above."
            testId="trackers-empty-room"
          />
        ) : presentation === 'table' ? (
          <TrackerGridSurface
            rows={orderedRows}
            trackerType={effectiveDefinition.selectedType}
            columnConfig={viewState.columnConfig}
            sortBy={effectiveDefinition.sortBy}
            sortDirection={effectiveDefinition.sortDirection}
            columnFilters={viewState.columnFilters}
            onColumnFiltersChange={setColumnFilters}
            isRowEditable={ROW_EDITABLE}
            onItemsUpdate={updateGridItems}
            selectedItemId={activeItemId}
            onOpenItem={openItem}
            // Also what turns the pinned overflow column back on; see
            // `useTrackerRowMenu`.
            onRowContextMenu={rowMenu.openForRows}
            loaded={loaded}
          />
        ) : presentation === 'kanban' ? (
          <TrackerBoardSurface
            rows={orderedRows}
            trackerType={effectiveDefinition.selectedType}
            groupBy={effectiveDefinition.groupBy}
            ordering={effectiveDefinition.ordering}
            statusScope={effectiveDefinition.statusScope}
            // The open item, not a checkbox selection: this surface has no
            // multi-select, so `highlightedItemId` is the right channel.
            highlightedItemId={activeItemId}
            onOpenItem={openItem}
            onItemUpdate={updateBoardItem}
            currentIdentity={identity}
          />
        ) : presentation === 'radar' ? (
          <TrackerRadarView
            items={orderedRows}
            currentIdentity={identity}
            selectedItemId={activeItemId}
            onItemSelect={openItem}
            onOpenDocument={openItem}
          />
        ) : presentation === 'timeline' ? (
          <TrackerTimelineView
            items={orderedRows}
            groupBy={effectiveDefinition.groupBy}
            ordering={effectiveDefinition.ordering}
            selectedItemId={activeItemId}
            onItemSelect={openItem}
            onOpenDocument={openItem}
            resolveRelationshipLabel={resolveRelationshipLabel}
          />
        ) : presentation === 'tag-board' ? (
          <TagBoard
            items={orderedRows}
            selectedItemId={activeItemId}
            onItemSelect={openItem}
            onOpenDocument={openItem}
          />
        ) : (
          <TrackerListView
            rows={orderedRows}
            stacked={phone}
            showType={selectedType === 'all'}
            groupBy={effectiveDefinition.groupBy}
            selectedItemId={activeItemId}
            onOpenItem={openItem}
            onRowContextMenu={rowMenu.openForItem}
            loaded={loaded}
          />
        )}
      </div>
    </div>
  );
}
